// @flow

import {Heading} from 'docs/components';
import CopyButton from 'docs/components/contrib/CopyButton/CopyButton';
import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import Badge from 'sail/Badge';
import Box from 'sail/Box';
import Group from 'sail/Group';
import HoverHandler from 'sail/handler/HoverHandler';
import Icon from 'sail/Icon';
import {Text} from 'sail/Text';
import {InlineLink} from 'sail/InlineLink';
import {ExpandableDetailsButton} from './ExpandableDetailsButton';

export type RoleDescriptionProps = {|
  roleName: React.Node,
  isBeta: boolean,
  roleExternalId: string,
  noteAboutRole?: React.Node,
  descriptionOfWhatRoleCanDo: React.Node,
  descriptionOfWhatRoleCannotDo: React.Node,
  someThingsThisRoleCanDo: Array<React.Node>,
  someThingsThisRoleCannotDo: Array<React.Node>,
|};

type RoleDescriptionsContainerProps = {|
  groupName: React.Node,
  groupIcon: React.Node,
  groupDescription?: React.Node,
  groupFootnote?: React.Node,
  roleDescriptions: Array<RoleDescriptionProps>,
|};

const messages = defineMessages({
  betaBadge: {
    id: 'docsApp.components.contrib.roleDescription.betaBadge',
    description: 'The text of a badge indicating this role is in beta.',
    defaultMessage: 'Beta',
  },
  someThingsThisRoleCanDo: {
    id: 'docsApp.components.contrib.roleDescription.someThingsThisRoleCanDo',
    description:
      'The text above a bulleted list that describes what the role can do.',
    defaultMessage: 'Some of what this role can do:',
  },
  someThingsThisRoleCannotDo: {
    id: 'docsApp.components.contrib.roleDescription.someThingsThisRoleCannotDo',
    description: "The text above a bulleted list of things the role can't do.",
    defaultMessage: "Some of what this role can't do:",
  },
  ssoRoleID: {
    id: 'docsApp.components.contrib.roleDescription.ssoRoleID',
    description: 'Text explaining that the following value is the role id.',
    defaultMessage: '<SSOLink>SSO</SSOLink> Role ID:',
  },
});

const renderExpandedDetails = (
  someThingsThisRoleCanDo: Array<React.Node>,
  someThingsThisRoleCannotDo: Array<React.Node>,
) => {
  return (
    <Group spacing={12} padding={{top: 20}}>
      <Group direction="horizontal" spacing={8} alignItems="baseline">
        <Icon icon="check" size={12} color="gray400" />
        <Text weight="medium">
          <FormattedMessage {...messages.someThingsThisRoleCanDo} />
        </Text>
      </Group>
      <Box padding={{horizontal: 32}}>
        <ul type="bulleted" size="small">
          {someThingsThisRoleCanDo.map((thing) => (
            <li>
              <Text lineHeight={24} color="gray700">
                {thing}
              </Text>
            </li>
          ))}
        </ul>
      </Box>

      <Group direction="horizontal" spacing={8} alignItems="baseline">
        <Icon icon="cancel" size={12} color="gray400" />
        <Text weight="medium">
          <FormattedMessage {...messages.someThingsThisRoleCannotDo} />
        </Text>
      </Group>
      <Box padding={{horizontal: 32}}>
        <ul type="bulleted" size="small">
          {someThingsThisRoleCannotDo.map((thing) => (
            <li>
              <Text lineHeight={24} color="gray700">
                {thing}
              </Text>
            </li>
          ))}
        </ul>
      </Box>
    </Group>
  );
};

type CopyRoleIdButtonProps = {
  textToCopy: string,
  copied: boolean,
  onClick: () => void,
};

function CopyRoleIdButton({
  textToCopy,
  copied,
  onClick,
}: CopyRoleIdButtonProps) {
  return (
    <HoverHandler>
      {(hoverProps, isHovering) => (
        <Box
          style={{
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'all 200ms',
          }}
          flex={{
            direction: 'row',
            alignItems: 'center',
          }}
          padding={4}
          divider={{side: 'all', color: 'light', width: 1}}
          onClick={onClick}
          /* eslint-disable no-nested-ternary */
          background={copied ? 'blue100' : isHovering ? 'gray50' : undefined}
          {...hoverProps}
        >
          <Group
            direction="horizontal"
            alignItems="center"
            spacing={4}
            padding={{right: 4}}
          >
            <Text typeface="mono" color="gray500" size={12}>
              {textToCopy}
            </Text>
          </Group>
          <Icon icon="clipboard" color="gray200" size={14} />
        </Box>
      )}
    </HoverHandler>
  );
}

export const RoleDescriptionsContainer = ({
  roleDescriptions,
  groupName,
  groupIcon,
  groupDescription,
  groupFootnote,
}: RoleDescriptionsContainerProps) => {
  const descriptions = roleDescriptions.map((description, index) => {
    return (
      <Box
        background={index % 2 === 0 ? 'white' : 'offset'}
        divider={{side: 'top', width: 1, color: 'light'}}
      >
        <RoleDescription {...description} />
      </Box>
    );
  });

  return (
    <Box
      divider={{side: 'all', color: 'light', width: 1}}
      style={{borderRadius: '5px'}}
      padding={2}
      margin={{bottom: 48}}
    >
      <Box margin={{top: 8}} padding={12}>
        <Group spacing={8}>
          <Group direction="horizontal" spacing={12} alignItems="center">
            {groupIcon}
            <Text size={20} weight="medium">
              {groupName}
            </Text>
          </Group>
          <Text lineHeight={16} size={16} color="gray500">
            {groupDescription}
          </Text>
        </Group>
      </Box>
      <Box
        divider={{side: 'all', width: 1, color: 'light'}}
        style={{borderRadius: '5px'}}
      >
        {descriptions}
      </Box>
      {groupFootnote && (
        <Box
          divider={{side: 'top', width: 1, color: 'light'}}
          margin={{top: 8}}
          padding={12}
        >
          <Text lineHeight={16} size={16} color="gray500">
            Note: {groupFootnote}
          </Text>
        </Box>
      )}
    </Box>
  );
};

const RoleDescription = ({
  roleName,
  roleExternalId,
  isBeta,
  noteAboutRole,
  descriptionOfWhatRoleCanDo,
  descriptionOfWhatRoleCannotDo,
  someThingsThisRoleCanDo,
  someThingsThisRoleCannotDo,
}: RoleDescriptionProps) => {
  return (
    <Box flex={{direction: 'row'}} padding={16}>
      <Group flexBasis="25%" spacing={8}>
        <Heading level={5} id={roleExternalId} anchored>
          {roleName}
        </Heading>

        {isBeta && (
          <Badge
            label={<FormattedMessage {...messages.betaBadge} />}
            color="blue"
            icon="beta"
            iconPosition="left"
          />
        )}
      </Group>
      <Group flexBasis="75%" spacing={8}>
        {noteAboutRole && (
          <Group direction="horizontal" spacing={8} alignItems="baseline">
            <Icon icon="info" color="gray300" size={12} />
            <Text lineHeight={20} size={14}>
              {noteAboutRole}
            </Text>
          </Group>
        )}
        <Group direction="horizontal" spacing={8} alignItems="baseline">
          <Icon icon="checkmark" color="green" size={12} />
          <Text lineHeight={20} size={14}>
            {descriptionOfWhatRoleCanDo}
          </Text>
        </Group>
        <Group direction="horizontal" spacing={8} alignItems="baseline">
          <Icon icon="cross" color="red" size={12} />
          <Text lineHeight={20} size={14}>
            {descriptionOfWhatRoleCannotDo}
          </Text>
        </Group>
        <Group alignItems="center" direction="horizontal" spacing={8}>
          <Icon icon="api" size={14} color="gray300" />
          <Text size={14}>
            <FormattedMessage
              {...messages.ssoRoleID}
              values={{
                SSOLink: (msg) => (
                  <InlineLink href="/docs/dashboard/sso">{msg}</InlineLink>
                ),
              }}
            />
          </Text>
          <CopyButton
            textToCopy={roleExternalId}
            CustomCopyButton={CopyRoleIdButton}
          />
        </Group>

        <Box padding={{top: 8}}>
          <ExpandableDetailsButton>
            {renderExpandedDetails(
              someThingsThisRoleCanDo,
              someThingsThisRoleCannotDo,
            )}
          </ExpandableDetailsButton>
        </Box>
      </Group>
    </Box>
  );
};
